// web/ui.ts — thin DOM layer. Owns screen switching, the toast, and the HUD
// render functions. All game logic lives in game.ts; this only reflects state
// into the static markup in index.html.

import { CASTE_NAME } from './game.ts';
import type { GameSession, EquipmentLayout } from './game.ts';
import { BOTTLENECK, GROWTH_TREND } from '../src/sim/diagnostics.ts';
import { ADVICE } from '../src/sim/advisor.ts';
import type { GeneAdvice } from '../src/sim/advisor.ts';
import { CASTE_GLYPH, CASTE_NAME as CASTE_LABEL, primaryCasteForType, casteCompatLabel } from '../src/sim/casteGenes.ts';
import { rarityPerk } from '../src/sim/rarityPerks.ts';
import { rarityBandHTML, statLinesHTML, generationHTML, powerHTML, fmtPower } from './geneCard.ts';
import { colonyColor, generationColor } from './render.ts';
import { loadRecords, effectiveSnapshot } from './colonyPower.ts';
import { buildCompletion } from './completion.ts';
import type { BuildCompletion } from './completion.ts';
import { shortRewardName } from './evolutionBox.ts';
import type { OpenResult, RewardGene } from './evolutionBox.ts';
import { rarityFlourish } from './boxReveal.ts';
import type { Discovery } from './boxReveal.ts';
import { audio } from './audio.ts';
import type { CueId } from './audio.ts';
import { EVENT_META, chronicleText } from './history.ts';
import type { ColonyHistory, HistoryEvent, Snapshot } from './history.ts';
import type { WatchView } from './watch.ts';
import type { ThreatLevel } from './wildlifeData.ts';
import { ROUTE_TIER_COLORS } from './logistics.ts';
import type { LogisticsView } from './logistics.ts';

/** Get an element by id or throw (fail fast if markup drifts). */
export function byId<T extends HTMLElement = HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`missing #${id}`);
  return el as T;
}

const SCREENS = ['menu', 'settings', 'howto', 'game', 'onboard'] as const;
export type ScreenName = (typeof SCREENS)[number];

/** Identity accent colour per TRAIT.* (Aggressive, Expansionist, Industrial,
 *  Defensive, Adaptive, Predatory); grey is the still-forming fallback. */
const IDENTITY_COLOR: readonly string[] = [
  '#ff5d5d', // Aggressive
  '#ffb13d', // Expansionist
  '#5dd6a8', // Industrial
  '#6b9bff', // Defensive
  '#b06bff', // Adaptive
  '#ff6fd0', // Predatory
];
const IDENTITY_FORMING_COLOR = '#7d8795';

/** PRESSURE_STATE.OverCapacity (src/sim/capacity.ts) — the over-capacity band. */
const PRESSURE_STATE_OVER = 2;

/** Show exactly one top-level screen (others hidden). 'loading' is handled separately. */
export function showScreen(name: ScreenName): void {
  for (const s of SCREENS) byId(`screen-${s}`).hidden = s !== name;
}

export function setLoading(on: boolean): void {
  byId('loading').hidden = !on;
}

let toastTimer = 0;
export function showToast(message: string, ms = 2300): void {
  const t = byId('toast');
  t.textContent = message;
  t.hidden = false;
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { t.hidden = true; }, ms) as unknown as number;
}

function setText(id: string, value: string | number): void {
  byId(id).textContent = String(value);
}


// --- HUD renderers ----------------------------------------------------------

export function updateTopbar(session: GameSession): void {
  const { rank, of } = session.playerRank();
  setText('hud-region', session.currentRegionName());
  setText('hud-tick', session.tick);
  setText('hud-rank', `#${rank}/${of}`);
}

/**
 * Render the colony's TOTAL POWER read-model into the HUD: the topbar pill, the "Your
 * Colony" power banner (the colony total + each caste's contribution), and the
 * leaderboard power strip. One pure read of `session.powerSnapshot()` — which is derived
 * from each caste's resolved GENOME profile, so it is independent of living population and
 * persists through population losses — plus the persisted all-time-high colony power
 * (web/colonyPower.ts records; display-only, never part of save/replay). The best is
 * clamped to at least the live value so a gene's between-applies generational ramp can
 * never read as "above the all-time best".
 */
export function updateColonyPower(session: GameSession): void {
  // The session exposes RAW power (the apply budget + adaptive-loot need vector read it);
  // the PLAYER sees it through the DIMINISHING-returns display curve, so early gains feel
  // major and late gains feel minor while progression stays infinite (web/colonyPower.ts).
  const p = effectiveSnapshot(session.powerSnapshot());
  setText('hud-power', fmtPower(p.colony));
  setText('cp-total', fmtPower(p.colony));
  setText('cp-worker', fmtPower(p.worker));
  setText('cp-soldier', fmtPower(p.soldier));
  setText('cp-flyer', fmtPower(p.flyer));
  setText('cp-specialist', fmtPower(p.specialist));
  // Records are stored RAW; show the best through the SAME curve, clamped to at least the
  // live value (a between-applies generational ramp must never read as above the best).
  const records = loadRecords();
  const bestColony = effectiveSnapshot(records ?? { worker: 0, soldier: 0, flyer: 0, specialist: 0 }).colony;
  setText('cp-best', fmtPower(Math.max(bestColony, p.colony)));
  setText('lb-power-val', fmtPower(p.colony));
}

export function updateColonyPanel(session: GameSession): void {
  const info = session.playerInfo();

  // Emergent identity — the colony's recognizable personality, shown prominently.
  const idn = info.identity;
  const col = idn.emerged ? (IDENTITY_COLOR[idn.trait] ?? IDENTITY_FORMING_COLOR) : IDENTITY_FORMING_COLOR;
  const badge = byId('colony-identity');
  badge.classList.toggle('emerged', idn.emerged);
  badge.style.setProperty('--idc', col);
  badge.title = idn.descriptor;
  badge.innerHTML =
    `<span class="identity-badge">${idn.emerged ? '🧬' : '◌'} ${escapeHtml(idn.name)}${idn.emerged ? ' Colony' : '…'}</span>` +
    `<span class="identity-desc">${escapeHtml(idn.descriptor)}</span>`;

  // Active ecosystem event — the seasonal force (flood/heat/pesticide/…) bending the
  // world right now, so a swing in food or pressure has a visible cause.
  updateEcoEvent(session);

  setText('colony-pop', info.population);
  setText('colony-food', info.food);
  setText('colony-genes', info.genes);
  setText('colony-mil', info.military);
  setText('colony-hives', info.hives);
  setText('colony-regions', info.regionsHeld);

  // Carrying capacity + population pressure (the stability foundation).
  const capInfo = session.capacityInfo();
  setText('cap-num', `${capInfo.population} / ${capInfo.capacity}`);
  const capEl = byId('colony-capacity');
  capEl.classList.toggle('over', capInfo.state === PRESSURE_STATE_OVER);
  const pct = Math.max(0, Math.min(100, Math.round(capInfo.pressure / 10)));
  byId('cap-fill').style.width = `${pct}%`;
  byId('cap-fill').classList.toggle('over', capInfo.state === PRESSURE_STATE_OVER);
  setText('cap-state', capInfo.stateName);

  // Bottleneck diagnostics — WHY growth has stalled (observe-only read-model).
  updateDiagnostics(session);

  // Nest pressure — the live wildlife threat the colony is adapting to (observe-only).
  byId('colony-pressure').innerHTML = pressureBlockHTML(session.pressureInfo());

  // Territorial establishment — how far the colony has developed each region (and the
  // carrying capacity that settled land grants). Surfaces the live TerritoryDriver.
  updateTerritory(session);

  const c = info.castes;
  byId('colony-castes').innerHTML =
    `<span class="chip">🔨 ${c.worker} W</span>` +
    `<span class="chip">⚔ ${c.soldier} S</span>` +
    `<span class="chip">🪽 ${c.flyer} F</span>` +
    `<span class="chip">✦ ${c.specialist} Sp</span>`;

  // Hive census: a full per-caste readout plus brood-ready Larvae and the queen.
  const cs = session.hiveCensus();
  byId('hive-census').innerHTML =
    censusRow('🔨', 'Workers', cs.worker) +
    censusRow('⚔', 'Soldiers', cs.soldier) +
    censusRow('🪽', 'Flyers', cs.flyer) +
    censusRow('✦', 'Specialists', cs.specialist) +
    censusRow('🥚', 'Larvae', cs.larvae) +
    censusRow('👑', 'Queens', cs.queens);

  // Queen vitality bar — she anchors the colony and never dies, but a sustained nest
  // siege wears her HP down (recovering when calm). Shown only when a queen ENTITY
  // exists; emphasized when imperiled (below a quarter HP).
  updateQueenHealth(cs);

  const statusEl = byId('colony-status');
  // Recovery + lone-queen rebuilding both show the amber "in trouble" badge; only a
  // truly healthy colony reads green.
  const cls = cs.status === 'Extinct' ? 'extinct'
    : (cs.status === 'Recovery' || (cs.alive && session.colonyPopulation(0) === 0)) ? 'rebuilding'
    : 'thriving';
  statusEl.textContent = cs.status;
  statusEl.className = `status ${cls}`;

  drawPopTrend(session);
}

/** Per-event icon (keyed by `ecoEventName`); falls back to a generic storm glyph. */
const EVENT_ICON: Record<string, string> = {
  Pesticide: '☠️', HeatWave: '🔥', Flooding: '🌊', PredatorMigration: '🦅', FoodBloom: '🌸',
};

/**
 * Active-ecosystem-event banner: names the seasonal force bending the world (flood / heat /
 * pesticide / predator migration / food bloom), the stats it's pushing up or down, and how
 * long it lasts — so a sudden swing in food or pressure reads as a cause, not a mystery.
 * Hidden when nothing is active. Pure render of `session.eventInfo()`.
 */
function updateEcoEvent(session: GameSession): void {
  const el = byId('colony-event');
  const e = session.eventInfo();
  if (e === null) { el.hidden = true; return; }
  el.hidden = false;
  const fx: string[] = [];
  if (e.foodRegenPct !== 100) fx.push(`🍖 ${e.foodRegenPct > 100 ? 'up' : 'down'}`);
  if (e.wildlifeCapPct !== 100) fx.push(`🐾 ${e.wildlifeCapPct > 100 ? 'up' : 'down'}`);
  if (e.geneDropPct !== 100) fx.push(`🧬 ${e.geneDropPct > 100 ? 'up' : 'down'}`);
  if (e.organismDrain > 0) fx.push('💀 attrition');
  el.innerHTML =
    `<b>${EVENT_ICON[e.name] ?? '🌪'} ${escapeHtml(e.name)}</b>` +
    `<span class="ev-fx">${escapeHtml(fx.join(' · ') || 'shifting the ecosystem')}</span>` +
    `<span class="ev-rem">${e.remaining}t left</span>`;
}

/**
 * Territorial establishment block: the established/integrated province counts, the
 * carrying-capacity bonus settled land grants, and a per-region stage strip (one pip per
 * region, brightening Unexplored→Integrated) with a name/stage/progress tooltip. Pure
 * render of `session.territoryInfo()`.
 */
function updateTerritory(session: GameSession): void {
  const t = session.territoryInfo();
  setText('terr-summary', `${t.establishedCount} established · ${t.integratedCount} integrated`);
  setText('terr-cap', t.capacityBonus > 0 ? `+${t.capacityBonus} cap` : '');
  byId('terr-strip').innerHTML = t.regions
    .map((r) => `<span class="terr-pip s${r.stage}" title="${escapeHtml(r.name)}: ${escapeHtml(r.stageName)} (${r.progressPct}%)"></span>`)
    .join('');
}

/**
 * Queen vitality bar: she anchors the colony and never dies, but a sustained nest siege
 * wears her HP down (she recovers when calm). Shown only when a queen ENTITY exists
 * (queenMaxHp > 0); emphasized (red + pulse) when imperiled. Pure render of the census.
 */
function updateQueenHealth(cs: { queenHp: number; queenMaxHp: number; queenImperiled: boolean }): void {
  const el = byId('colony-queen');
  if (cs.queenMaxHp <= 0) { el.hidden = true; return; }
  el.hidden = false;
  setText('queen-num', `${cs.queenHp} / ${cs.queenMaxHp}`);
  const pct = Math.max(0, Math.min(100, Math.round((cs.queenHp / cs.queenMaxHp) * 100)));
  const fill = byId('queen-fill');
  fill.style.width = `${pct}%`;
  fill.classList.toggle('imperiled', cs.queenImperiled);
  el.classList.toggle('imperiled', cs.queenImperiled);
}

/**
 * The nest-pressure block: one bar per wildlife channel (🕷 predation, 🐦 aerial,
 * 🐀 theft) the colony is adapting to, or a "calm" note when nothing is pressing. Bars
 * scale relative to the strongest channel (floored at 8 so a light threat doesn't fill
 * the bar); the raw integer magnitude is shown alongside. Pure render of pressureInfo().
 */
function pressureBlockHTML(pr: { predation: number; aerial: number; theft: number; total: number }): string {
  if (pr.total <= 0) return '<div class="pr-calm muted small">🕸 Calm — no wildlife pressure on the nest</div>';
  const denom = Math.max(8, pr.predation, pr.aerial, pr.theft);
  const row = (glyph: string, label: string, val: number, cls: string): string => {
    const w = Math.max(0, Math.min(100, Math.round((val / denom) * 100)));
    return '<div class="pr-row">' +
      `<span class="pr-label">${glyph} ${label}</span>` +
      `<span class="pr-bar"><span class="pr-fill ${cls}" style="width:${w}%"></span></span>` +
      `<b class="pr-val">${val}</b></div>`;
  };
  return row('🕷', 'Predation', pr.predation, 'pred') +
    row('🐦', 'Aerial', pr.aerial, 'aerial') +
    row('🐀', 'Theft', pr.theft, 'theft');
}

/** CSS severity class per BOTTLENECK.* — drives the badge colour (see styles.css). */
const BOTTLENECK_SEVERITY: readonly string[] = [
  'ok',     // None
  'danger', // Food
  'warn',   // Storage
  'warn',   // Worker
  'warn',   // Larvae
  'danger', // Defense
  'info',   // Expansion
];

/**
 * Render the bottleneck diagnostics block: the limiter badge (coloured by severity),
 * the expected growth (or Stalled/Declining), capacity utilisation %, and the
 * resource-flow row + bar. Reads the pure observe-only read-model (game.diagnosticsInfo).
 */
function updateDiagnostics(session: GameSession): void {
  const d = session.diagnosticsInfo();

  // Bottleneck badge — text = limiter name, class = severity.
  const badge = byId('diag-badge');
  badge.textContent = d.bottleneckName;
  badge.className = `diag-badge ${BOTTLENECK_SEVERITY[d.bottleneck] ?? 'ok'}`;
  badge.title = `Binding growth limiter: ${d.bottleneckName}`;

  // Secondary co-limiters — the OTHER active limiters beyond the primary, so the player
  // sees "Food AND Defense both bind", not just the most-binding one. Names come straight
  // from the read-model (limiterNames[0] is the primary already shown in the badge; the
  // rest are the co-limiters). Hidden when only one (or none) binds.
  const secEl = byId('diag-secondary');
  if (d.secondaryBottlenecks.length > 0) {
    secEl.hidden = false;
    secEl.textContent = `⛓ Also limited by ${d.limiterNames.slice(1).join(' · ')}`;
  } else {
    secEl.hidden = true;
  }

  // Expected growth: a positive rate when unblocked, else the trend word.
  const growthEl = byId('diag-growth');
  if (d.bottleneck === BOTTLENECK.None) {
    growthEl.textContent = `+${d.expectedGrowthPer100}/100t`;
    growthEl.className = 'diag-growth grow';
  } else if (d.trend === GROWTH_TREND.Declining) {
    growthEl.textContent = 'Declining';
    growthEl.className = 'diag-growth declining';
  } else if (d.trend === GROWTH_TREND.Stable) {
    growthEl.textContent = 'At capacity';
    growthEl.className = 'diag-growth stable';
  } else {
    growthEl.textContent = 'Stalled';
    growthEl.className = 'diag-growth stalled';
  }

  // Capacity utilisation % (permille / 10).
  setText('diag-util', `${Math.max(0, Math.round(d.capacityUtilPermille / 10))}%`);

  // Resource flow row.
  setText('diag-in', d.foodIncome);
  setText('diag-store', d.foodStore);
  setText('diag-out', d.foodConsumeEst);

  // Flow bar: income vs consume as two proportional segments (deterministic-irrelevant
  // visuals). Scale to the larger of the two so the dominant flow fills the bar.
  const span = Math.max(1, d.foodIncome, d.foodConsumeEst);
  const inPct = Math.max(0, Math.min(100, Math.round((d.foodIncome / span) * 100)));
  const outPct = Math.max(0, Math.min(100, Math.round((d.foodConsumeEst / span) * 100)));
  byId('diag-bar-in').style.width = `${inPct}%`;
  byId('diag-bar-out').style.width = `${outPct}%`;
  // Net-negative flow tints the bar container as a warning.
  byId('colony-diagnostics').classList.toggle('starving', d.foodNet < 0);
}

/** One labelled census row: emoji + label on the left, count on the right. */
function censusRow(glyph: string, label: string, value: number): string {
  return `<div class="crow"><span class="clabel">${glyph} ${label}</span><span class="cval">${value}</span></div>`;
}

/**
 * Paint the population trend as an autoscaled polyline on #pop-trend. UI-only:
 * a faint baseline, then an accent stroke over the samples. Degrades gracefully
 * when there are fewer than two samples (clears + draws the baseline only).
 */
export function drawPopTrend(session: GameSession): void {
  const canvas = byId<HTMLCanvasElement>('pop-trend');
  const g = canvas.getContext('2d');
  if (!g) return;
  const w = canvas.width;
  const h = canvas.height;
  g.clearRect(0, 0, w, h);

  // Faint baseline along the bottom.
  g.strokeStyle = 'rgba(139,151,167,0.35)';
  g.lineWidth = 1;
  g.beginPath();
  g.moveTo(0, h - 1.5);
  g.lineTo(w, h - 1.5);
  g.stroke();

  const data = session.populationTrend();
  if (data.length < 2) return;

  // Autoscale to the sample range; guard a flat series (max === min).
  let min = Infinity;
  let max = -Infinity;
  for (const v of data) { if (v < min) min = v; if (v > max) max = v; }
  const span = max - min || 1;
  const padY = 4;
  const usableH = h - padY * 2;
  const stepX = data.length > 1 ? w / (data.length - 1) : w;

  g.strokeStyle = '#46e0d0'; // --player accent
  g.lineWidth = 1.5;
  g.beginPath();
  for (let i = 0; i < data.length; i++) {
    const v = data[i] as number;
    const x = i * stepX;
    const y = padY + (1 - (v - min) / span) * usableH;
    if (i === 0) g.moveTo(x, y);
    else g.lineTo(x, y);
  }
  g.stroke();
}

/**
 * Left-panel GENERATIONS readout (render-only): the colony's current generation
 * (what new offspring are now born as), the share of the living population that is
 * that newest generation, and one bar per living generation. Bar colour matches
 * the canvas overlay ramp (by lineage AGE), so the panel and the map agree. The
 * current generation's row is highlighted. Reads the live census every refresh.
 */
export function updateGenerations(session: GameSession): void {
  const census = session.generationCensus();
  setText('gen-current', `Generation ${census.current}`);
  setText('gen-share', `${census.latestShare}% newest gen`);

  const binsEl = byId('gen-bins');
  if (census.bins.length === 0) {
    binsEl.innerHTML = '<div class="gen-empty muted small">No living members yet.</div>';
    return;
  }
  // Show the newest MAX_GEN_BINS generations individually; fold any older ones (bins
  // are ascending, so the oldest are first) into one collapsed "older" row, so the
  // panel stays compact no matter how many times a long-lived colony has evolved.
  const all = census.bins;
  const cut = all.length > MAX_GEN_BINS ? all.length - MAX_GEN_BINS : 0;
  const shown = all.slice(cut);
  const older = all.slice(0, cut);
  const olderCount = older.reduce((s, b) => s + b.count, 0);
  // Scale bars to the busiest DISPLAYED row (shown bins + the collapsed bucket).
  let max = olderCount;
  for (const b of shown) if (b.count > max) max = b.count;
  let html = '';
  if (older.length > 0) {
    html += generationOlderRow(olderCount, older[older.length - 1]?.generation ?? 0, max);
  }
  html += shown.map((b) => generationBinRow(b.generation, b.count, max, census.current)).join('');
  binsEl.innerHTML = html;
}

/** Newest generations shown individually in the panel; older ones collapse into one row. */
const MAX_GEN_BINS = 8;

/** The collapsed "older generations" row: every generation older than the shown window. */
function generationOlderRow(count: number, throughGen: number, max: number): string {
  const pct = max > 0 ? Math.max(4, Math.round((100 * count) / max)) : 0;
  return '<div class="gen-row older">' +
    `<span class="gen-label muted">≤ Gen ${throughGen}</span>` +
    `<span class="gen-track"><span class="gen-bar" style="width:${pct}%;background:#5b5570"></span></span>` +
    `<span class="gen-count">${count}</span>` +
    '</div>';
}

/** One generation bar row: label + count, with a bar coloured by lineage age. */
function generationBinRow(generation: number, count: number, max: number, current: number): string {
  const color = generationColor(current - generation);
  const pct = max > 0 ? Math.max(4, Math.round((100 * count) / max)) : 0;
  const isCurrent = generation === current;
  return `<div class="gen-row${isCurrent ? ' current' : ''}">` +
    `<span class="gen-label">Gen ${generation}</span>` +
    `<span class="gen-track"><span class="gen-bar" style="width:${pct}%;background:${color}"></span></span>` +
    `<span class="gen-count">${count}</span>` +
    '</div>';
}

/**
 * Left-panel LOGISTICS readout (render-only, pure reads of the live route field):
 * route-efficiency %, a tier legend whose swatches MATCH the canvas colours, the
 * per-tier tile counts, smoothed throughput, and a bottleneck indicator that turns
 * to a ⚠ warning when supply is being squeezed through a constriction.
 */
export function updateLogistics(session: GameSession, logistics: LogisticsView | null): void {
  if (!logistics) return;
  void session; // reserved for future per-colony breakdowns; keeps the call shape stable
  const eff = logistics.routeEfficiency();
  const counts = logistics.routeCounts();
  const thru = logistics.throughput();
  const bn = logistics.bottleneckCount();

  setText('log-efficiency', `${eff}%`);
  const fill = byId('log-eff-fill');
  fill.style.width = `${Math.max(0, Math.min(100, eff))}%`;

  byId('log-legend').innerHTML =
    routeLegendRow(1, 'Secondary', counts.secondary) +
    routeLegendRow(2, 'Primary', counts.primary) +
    routeLegendRow(3, 'Major Supply Route', counts.major);

  setText('log-throughput', String(thru));

  const bnEl = byId('log-bottleneck');
  if (bn > 0) {
    bnEl.classList.add('warn');
    bnEl.innerHTML = `<span class="log-bn-icon">⚠</span> ${bn} bottleneck${bn === 1 ? '' : 's'}`;
  } else {
    bnEl.classList.remove('warn');
    bnEl.innerHTML = '<span class="log-bn-icon ok">✓</span> no bottlenecks';
  }
}

/** One legend row: a swatch in the canvas tier colour, the tier name, its count. */
function routeLegendRow(tier: number, label: string, count: number): string {
  const color = ROUTE_TIER_COLORS[tier] ?? 'transparent';
  return '<div class="log-legrow">' +
    `<span class="log-swatch tier-${tier}" style="background:${color}"></span>` +
    `<span class="log-leglabel">${escapeHtml(label)}</span>` +
    `<span class="log-legcount">${count}</span>` +
    '</div>';
}

export function updateObjectives(session: GameSession): void {
  const ul = byId('objectives');
  ul.innerHTML = session.objectiveStatus()
    .map((o) => `<li class="${o.done ? 'done' : ''}"><span class="box">${o.done ? '✓' : ''}</span><span>${escapeHtml(o.label)}</span></li>`)
    .join('');
}

export function updateLeaderboard(session: GameSession): void {
  const ol = byId('leaderboard');
  ol.innerHTML = session.leaderboard()
    .map((r, i) => {
      const who = r.isPlayer ? 'You' : `Rival ${r.colony}`;
      const idTag = r.identityEmerged
        ? `<span class="who-id">${escapeHtml(r.identity)}</span>`
        : '<span class="who-id forming">forming</span>';
      // Hover detail now also surfaces each colony's population pressure (pop/cap + state)
      // and the wildlife pressure it's under — so rival standings explain the "why", too.
      const cap = session.capacityInfo(r.colony);
      const pr = session.pressureInfo(r.colony);
      const title =
        `military ${r.military} · regions ${r.regions} · genes ${r.genes} · wars ${r.warsWon} · ` +
        `pop ${cap.population}/${cap.capacity} (${cap.stateName}) · ` +
        `pressure 🕷${pr.predation} 🐦${pr.aerial} 🐀${pr.theft}`;
      return `<li class="${r.isPlayer ? 'me' : ''}">` +
        `<span class="swatch" style="background:${colonyColor(r.colony)}"></span>` +
        `<span class="who">#${i + 1} ${who}${idTag}</span>` +
        `<span class="score" title="${title}">${r.score}</span>` +
        `</li>`;
    })
    .join('');
}

/**
 * Region-scale projects — the standing long-term objective. Each row shows costs,
 * rewards, live build progress, and a Start button (or its lock reason). The Start
 * click is wired by delegation in main.ts.
 */
export function updateProjects(session: GameSession): void {
  const el = byId('projects');
  el.innerHTML = session.projectViews().map((p) => {
    const pct = Math.round(p.progressPermille / 10);
    const cls = p.status === 2 ? 'complete' : p.status === 1 ? 'active' : '';
    const action = p.status === 2
      ? '<span class="proj-done">✓ Done</span>'
      : p.status === 1
        ? `<span class="proj-prog">${pct}%</span>`
        : (p.canStart
            ? `<button class="proj-start" data-project="${p.id}">Start</button>`
            : `<span class="proj-lock" title="${escapeHtml(p.lockReason)}">🔒</span>`);
    const bar = p.status === 1
      ? `<div class="proj-bar"><div class="proj-fill" style="width:${pct}%"></div></div>`
      : '';
    const lock = (p.status === 0 && !p.canStart)
      ? `<div class="proj-lockmsg">${escapeHtml(p.lockReason)}</div>` : '';
    return `<li class="proj ${cls}">` +
      `<div class="proj-head"><span class="proj-name">${escapeHtml(p.name)}</span>${action}</div>` +
      `<div class="proj-meta">${escapeHtml(p.regionName)} · 👷${p.minPopulation}+ · 🍖${p.foodCost} · ` +
      `+${p.foodDividend}/cyc · ★${p.leaderboardValue} · 🧬${p.energyReward}</div>` +
      bar + lock +
      `<div class="proj-blurb">${escapeHtml(p.blurb)}</div>` +
      `</li>`;
  }).join('');
}

export function updateRegions(session: GameSession): void {
  const ul = byId('regions');
  ul.innerHTML = session.regionViews()
    .map((r) => {
      const ctrl = r.controller < 0 ? '—' : (r.controlledByPlayer ? 'You' : `Rival ${r.controller}`);
      const lockTitle = r.unlocked ? 'unlocked' : `locks until strength ${r.unlockStrength}`;
      return `<li class="${r.unlocked ? '' : 'locked'}" title="${lockTitle}">` +
        `<span class="rglyph">${escapeHtml(r.glyph)}</span>` +
        `<span>${escapeHtml(r.name)}</span>` +
        `<span class="ctrl">${r.unlocked ? ctrl : '🔒'}</span>` +
        `</li>`;
    })
    .join('');
}

// --- Wildlife / apex HUD ----------------------------------------------------

/** CSS modifier class for a threat tier (drives the badge colour). */
function threatClass(threat: ThreatLevel): string {
  return `threat-${threat.toLowerCase()}`;
}

/** A small coloured threat badge. */
function threatBadge(threat: ThreatLevel): string {
  return `<span class="threat-badge ${threatClass(threat)}">${escapeHtml(threat)}</span>`;
}

/**
 * Right-panel Wildlife block: a per-species live census plus the Apex defeat
 * milestones (most-recent first). Both read live simulation state.
 */
export function updateWildlife(session: GameSession): void {
  const census = session.wildlifeCensus();
  const total = session.wildlifeTotal();
  byId('wildlife-census').innerHTML =
    census.map((c) => censusRow('🐾', c.name, c.count)).join('') +
    `<div class="crow total"><span class="clabel">Total wildlife</span><span class="cval">${total}</span></div>`;

  const defeats = session.apexDefeats(6);
  const ul = byId('apex-defeats');
  if (defeats.length === 0) {
    ul.innerHTML = '';
  } else {
    ul.innerHTML = '<li class="apex-defeats-head">☠ Apex milestones</li>' +
      [...defeats].reverse().map((d) => {
        const verdict = d.outcome === 'slain'
          ? (d.victor >= 0 ? `slain by ${d.victor === 0 ? 'You' : `Rival ${d.victor}`}` : 'slain')
          : 'escaped';
        return `<li class="apex-defeat ${d.outcome}">` +
          `<span class="adname">${escapeHtml(d.name)}</span>` +
          `<span class="adverdict">${escapeHtml(verdict)}</span>` +
          `<span class="muted small">t${d.tick}</span></li>`;
      }).join('');
  }
}

/**
 * The floating apex boss-alert banner over the stage. Visible only while a boss
 * is alive; shows kind, a live HP bar, and the colony leading the damage race.
 */
export function updateApex(session: GameSession): void {
  const banner = byId('apex-banner');
  const st = session.apexStatus();
  if (!st) { banner.hidden = true; return; }
  banner.hidden = false;
  setText('apex-name', st.name);
  const threatEl = byId('apex-threat');
  threatEl.textContent = st.topThreat >= 0
    ? `· threat: ${st.topThreat === 0 ? 'You' : `colony ${st.topThreat}`} (${st.topThreatDamage} dmg)`
    : '· no engagement yet';
  const frac = st.maxHp > 0 ? Math.max(0, Math.min(1, st.hp / st.maxHp)) : 0;
  byId('apex-hpfill').style.width = `${Math.round(frac * 100)}%`;
  setText('apex-hpnum', `${st.hp} / ${st.maxHp}`);
}

/**
 * Render the Wildlife Encyclopedia overlay: every species then every apex boss,
 * each card showing name, threat badge, LIVE count, blurb, what it DROPS, and the
 * EVOLUTIONARY OPPORTUNITY. All four are inspectable per the design brief.
 */
export function renderEncyclopedia(session: GameSession): void {
  const rows = session.wildlifeEncyclopedia();
  const species = rows.filter((r) => r.entry.kind === 'species');
  const apex = rows.filter((r) => r.entry.kind === 'apex');
  byId('enc-species').innerHTML = species.map(encCard).join('');
  byId('enc-apex').innerHTML = apex.map(encCard).join('');
}

/** One encyclopedia card. `live` is the joined live population count. */
function encCard(row: { entry: { name: string; blurb: string; threat: ThreatLevel; drops: string; evolution: string; kind: string }; liveCount: number }): string {
  const e = row.entry;
  const liveLabel = e.kind === 'apex'
    ? (row.liveCount > 0 ? '<span class="enc-live alive">● ALIVE NOW</span>' : '<span class="enc-live">not present</span>')
    : `<span class="enc-live">${row.liveCount} alive</span>`;
  return '<div class="enc-card-item">' +
    '<div class="enc-head">' +
    `<b class="enc-name">${escapeHtml(e.name)}</b>` +
    threatBadge(e.threat) +
    liveLabel +
    '</div>' +
    `<p class="enc-blurb">${escapeHtml(e.blurb)}</p>` +
    `<div class="enc-line"><span class="enc-tag">Drops</span> ${escapeHtml(e.drops)}</div>` +
    `<div class="enc-line"><span class="enc-tag evo">Evolution</span> ${escapeHtml(e.evolution)}</div>` +
    '</div>';
}

/** Serials currently selected for fusion (UI-owned, survives HUD re-renders). */
const fuseSelection = new Set<number>();
export function toggleFuse(serial: number): void {
  if (fuseSelection.has(serial)) fuseSelection.delete(serial);
  else if (fuseSelection.size < 3) fuseSelection.add(serial);
}
export function fuseSerials(): number[] {
  return [...fuseSelection];
}
export function clearFuse(): void {
  fuseSelection.clear();
}

/** CSS class per ADVICE.* verdict (index-aligned worst→best). */
const ADVICE_CLASS = ['poor', 'neutral', 'synergy', 'recommended'] as const;
/** Compact glyph per ADVICE.* verdict — a quick visual cue ahead of the label. */
const ADVICE_GLYPH = ['◇', '•', '◆', '★'] as const;
// Index-alignment contract: the class/glyph arrays are positional on ADVICE.*. If a
// verdict is added/reordered, these constants pin the worst→best ladder at compile time.
const _ADVICE_ALIGN: void = (() => {
  if (ADVICE_CLASS[ADVICE.Recommended] !== 'recommended' || ADVICE_CLASS[ADVICE.PoorFit] !== 'poor') {
    throw new Error('ADVICE_CLASS misaligned with ADVICE');
  }
})();

/**
 * The Evolution Advisor verdict badge for one gene row: a coloured chip carrying the
 * verdict name, with the full reason as a hover tooltip. Display-only — the advice is a
 * pure read-model (src/sim/advisor.ts). Renders nothing when no advice is attached.
 */
function adviceBadge(advice: GeneAdvice | undefined): string {
  if (!advice) return '';
  const cls = ADVICE_CLASS[advice.verdict] ?? 'neutral';
  const glyph = ADVICE_GLYPH[advice.verdict] ?? '';
  return (
    `<span class="advice-badge advice-${cls}" title="${escapeHtml(advice.reason)}">` +
    `${glyph} ${escapeHtml(advice.verdictName)}</span>`
  );
}

/**
 * The CASTE-COMPATIBILITY chip(s) for one gene row: which caste(s) this body part
 * suits, glyph + name, coloured per caste. ≥2 valid castes (a Hybrid organ) get a
 * distinct dashed "hybrid" wrapper. The row carries the valid caste INDICES (from the
 * pure read-model src/sim/casteGenes.ts) plus its joined label; glyph + name come from
 * the index-aligned CASTE_GLYPH / CASTE_NAME the same module exports, so the per-caste
 * colour class stays index-aligned. Display-only. Renders nothing without caste data.
 */
function casteCompatChip(castes: readonly number[] | undefined, label: string | undefined): string {
  if (!castes || castes.length === 0) return '';
  const hybrid = castes.length >= 2;
  const title = `Suits ${label ?? castes.join(', ')}`;
  const pills = castes
    .map((c) => `<span class="caste-pill caste-${c}">${CASTE_GLYPH[c] ?? '•'} ${escapeHtml(CASTE_LABEL[c] ?? `Caste ${c}`)}</span>`)
    .join('');
  return `<span class="caste-compat${hybrid ? ' caste-hybrid' : ''}" title="${escapeHtml(title)}">${pills}</span>`;
}

export function updateGenes(session: GameSession): void {
  const ul = byId('genes');
  const rows = session.geneRows();
  // Drop selections whose gene no longer exists (fused, evicted…).
  const present = new Set(rows.map((r) => r.serial));
  for (const s of [...fuseSelection]) if (!present.has(s)) fuseSelection.delete(s);

  if (rows.length === 0) {
    ul.innerHTML = '<li class="empty">No genes yet — your colony loots them from combat and deaths.</li>';
  } else {
    ul.innerHTML = rows.map((g) => {
      const badge = g.ascension > 0
        ? ` <span class="asc-badge" title="ascension tier ${g.ascension}">⬆${g.ascension} ${escapeHtml(g.ascendName)}</span>`
        : '';
      const ascend = g.canAscend
        ? `<button class="btn small ascend-gene" data-serial="${g.serial}" title="Evolve along its named ladder">Ascend ${g.ascendCost}⚡</button>`
        : '';
      const checked = fuseSelection.has(g.serial) ? ' checked' : '';
      const fit = adviceBadge(g.advice);
      const compat = casteCompatChip(g.castes, g.casteLabel);
      return '<li>' +
        `<input type="checkbox" class="fuse-pick" data-serial="${g.serial}"${checked} title="select for fusion (3 → 1)" />` +
        `<span class="glabel rarity-${g.rarity}">${escapeHtml(g.label)}${badge}</span>` +
        `<span class="gpower" title="Power Score — rarity · mutations · synergy · ascension · tier">⚡${fmtPower(g.power)}</span>` +
        compat +
        fit +
        ascend +
        `<button class="btn small apply-gene" data-gene-id="${g.id}">Apply</button>` +
        '</li>';
    }).join('');
  }
  setText('fuse-count', fuseSelection.size);
  byId<HTMLButtonElement>('btn-fuse').disabled = fuseSelection.size !== 3;
}

/**
 * The per-caste BUILD LOADOUT panels — caste progression framed as assembling a build.
 * A colony summary line (slots filled / unlocked + the caste to focus next) heads one
 * LOADOUT block per caste: a "<CASTE> BUILD" header with the NAMED equipped genes and the
 * "N / cap equipped" count, a completion bar, the single NEXT STEP, the equipped-gene rows
 * (empty entries read as open build slots), and the Unlock-slot button. Reads the pure
 * `equipmentLayout` + `completion.ts` read-models (no sim mutation) and feeds the topbar
 * "🧩 Build" pill from one roll-up. Refreshed via updateEquipment/updateEvolution.
 */
export function updateEquipment(session: GameSession): void {
  const host = byId('equipment');
  const layouts: EquipmentLayout[] = [];
  for (let caste = 0; caste < CASTE_NAME.length; caste++) layouts.push(session.equipmentLayout(caste));
  const build: BuildCompletion = buildCompletion(
    layouts.map((l) => ({ caste: l.caste, name: l.casteName, filled: l.occupied, cap: l.cap, max: l.max })),
  );

  // Topbar at-a-glance: filled / unlocked slots across the whole colony.
  setText('hud-build', `${build.totalFilled}/${build.totalCap}`);

  // Colony build summary — the headline "progress toward a build" line.
  const focusLay = build.focus >= 0 ? layouts[build.focus] : undefined;
  const focusTag = focusLay
    ? ` · focus next: <b class="bs-focus">${CASTE_GLYPH[build.focus] ?? '•'} ${escapeHtml(focusLay.casteName)}</b>`
    : ' · every caste complete ★';
  let html =
    '<div class="build-summary" title="Equip a gene of a new body part to fill a slot; unlock slots with Mutation Energy. Complete each caste\'s build.">' +
      `<span class="bs-main">Colony build <b>${build.totalFilled}/${build.totalCap}</b> slots filled</span>` +
      `<span class="bs-sub muted small">${build.complete}/${layouts.length} castes complete${focusTag}</span>` +
    '</div>';

  build.castes.forEach((c, caste) => {
    const lay = layouts[caste];
    if (!lay) return;
    // The loadout: one row per slot — a NAMED equipped gene (with its Power), an open
    // build slot, or a locked slot. The named genes read like a kit, not a bare grid.
    const rows = lay.slots.map((s) => {
      if (s.state === 'filled' && s.gene) {
        return `<div class="ld-item filled rarity-${s.gene.rarity}" title="${escapeHtml(s.gene.label)}">` +
          `<span class="ld-dot"></span>` +
          `<span class="ld-name">${escapeHtml(s.gene.label)}</span>` +
          `<span class="ld-power">⚡${fmtPower(s.gene.power)}</span></div>`;
      }
      if (s.state === 'empty') {
        return '<div class="ld-item open" title="Open build slot — equip a gene of a new body part">' +
          '<span class="ld-dot"></span><span class="ld-name muted">Open build slot</span></div>';
      }
      return '<div class="ld-item locked" title="Locked — unlock this slot to grow the build">' +
        '<span class="ld-dot"></span><span class="ld-name muted">Locked slot</span> 🔒</div>';
    }).join('');
    const btn = lay.cap >= lay.max
      ? '<button class="btn small eq-unlock" disabled>Max slots</button>'
      : `<button class="btn small eq-unlock" data-caste="${caste}"${lay.canUnlock ? '' : ' disabled'} ` +
        `title="Unlock one more equipment slot">Unlock slot (${lay.unlockCost}⚡)</button>`;
    const next = c.step === 'fill'
      ? `<span class="eq-next fill">⤷ ${escapeHtml(c.hint)}</span>`
      : c.step === 'unlock'
        ? '<span class="eq-next unlock">Slots full — unlock to grow this build</span>'
        : '<span class="eq-next complete">★ Build complete</span>';
    const buildName = `${lay.casteName.toUpperCase()} BUILD`;
    html += `<div class="eq-row ld-panel step-${c.step}${build.focus === caste ? ' focus' : ''}">` +
      '<div class="eq-head ld-head">' +
        `<b>${CASTE_GLYPH[caste] ?? '•'} ${escapeHtml(buildName)}</b>` +
        `<span class="eq-count">${lay.occupied}/${lay.cap} equipped</span>` +
      '</div>' +
      `<div class="comp-bar"><div class="comp-fill step-${c.step}" style="width:${Math.round(c.fillPct * 100)}%"></div></div>` +
      next +
      `<div class="ld-list">${rows}</div>` +
      `<div class="eq-actions">${btn}</div>` +
    '</div>';
  });
  host.innerHTML = html;
}

/** The gene progression tree overlay: a named ascension ladder per gene type. */
export function renderTree(session: GameSession): void {
  const tree = session.progressionTree();
  byId('tree-body').innerHTML = tree.map((t) => {
    const chips = t.ladder.map((name, i) =>
      `<span class="tier-chip${t.bestLevel >= i ? ' reached' : ''}">${escapeHtml(name)}</span>`,
    ).join('<span class="tier-arrow">→</span>');
    const beyond = t.bestLevel >= t.ladder.length
      ? `<span class="tier-arrow">→</span><span class="tier-chip reached">+${t.bestLevel - t.ladder.length + 1}</span>`
      : '';
    const status = t.owned > 0 ? `${t.owned} held · best ⬆${Math.max(0, t.bestLevel)}` : 'none held';
    return '<div class="tree-row">' +
      `<div class="tree-head"><b>${escapeHtml(t.baseName)}</b> <span class="muted small">${status}</span></div>` +
      `<div class="tree-ladder">${chips}${beyond}</div>` +
      '</div>';
  }).join('');
}

// --- Colony history / evolution timeline ------------------------------------

let histRef: ColonyHistory | null = null;
let histFilter = 'all';

/** Open/refresh the Colony History timeline overlay. */
export function renderHistory(history: ColonyHistory): void {
  histRef = history;
  histFilter = 'all';
  paintHistory();
}

export function setHistoryFilter(type: string): void {
  histFilter = type || 'all';
  paintHistory();
}

function paintHistory(): void {
  if (!histRef) return;
  const all = histRef.list();
  const counts: Record<string, number> = {};
  for (const e of all) counts[e.type] = (counts[e.type] ?? 0) + 1;
  const order = ['boom', 'victory', 'mutation', 'discovery', 'region', 'apex', 'extinction', 'milestone', 'founding'] as const;
  byId('hist-saga').innerHTML = sagaHTML(all);
  byId('hist-filters').innerHTML =
    histChip('all', 'All', all.length) +
    order.filter((t) => (counts[t] ?? 0) > 0).map((t) => histChip(t, EVENT_META[t].label, counts[t] ?? 0)).join('');
  const shown = histFilter === 'all' ? all : all.filter((e) => e.type === histFilter);
  byId('hist-body').innerHTML = shown.length
    ? [...shown].reverse().map(historyEventHTML).join('')
    : '<p class="muted">No events of this kind yet.</p>';
}

/** One-line saga summary above the filters: chapters, highlights, generation, span. */
function sagaHTML(all: readonly HistoryEvent[]): string {
  if (all.length === 0) return '<span class="muted">No chapters yet — the saga begins.</span>';
  const pinned = all.reduce((n, e) => n + (e.pinned ? 1 : 0), 0);
  let maxGen = 0;
  let lastTick = 0;
  for (const e of all) {
    if (e.gen !== undefined && e.gen > maxGen) maxGen = e.gen;
    if (e.tick > lastTick) lastTick = e.tick;
  }
  const gen = maxGen > 0 ? `${maxGen}` : '—';
  return `📖 ${all.length} chapters · ★ ${pinned} highlights · Generation ${escapeHtml(gen)} · ticks 0–${lastTick}`;
}

function histChip(type: string, label: string, n: number): string {
  return `<button class="hist-filter${histFilter === type ? ' active' : ''}" data-type="${type}">${escapeHtml(label)} <span class="muted">${n}</span></button>`;
}

function historyEventHTML(e: HistoryEvent): string {
  const m = EVENT_META[e.type];
  const chronicle = chronicleText(e);
  const genTag = e.gen !== undefined ? ` <span class="hist-gen">Gen ${e.gen}</span>` : '';
  let s = `<div class="hist-event type-${e.type}${e.pinned ? ' pinned' : ''}">` +
    `<span class="hist-icon" title="${escapeHtml(m.label)}">${m.icon}</span>` +
    '<div class="hist-main">' +
    `<div class="hist-title">${escapeHtml(e.title)} <span class="hist-tick">tick ${e.tick}</span>${genTag}</div>` +
    `<div class="hist-narrative">${escapeHtml(chronicle)}</div>`;
  // Keep the legacy detail below the featured prose, but only when it adds info
  // (i.e. there is a narrative and the detail differs — avoids duplicating one line).
  if (e.narrative && e.detail !== chronicle) s += `<div class="hist-detail">${escapeHtml(e.detail)}</div>`;
  if (e.snapshot) s += snapshotHTML(e.snapshot);
  return s + '</div></div>';
}

function snapshotHTML(s: Snapshot): string {
  const asc = s.maxAscension > 0 ? ` ⬆${s.maxAscension}` : '';
  const top = s.topGene && s.topGene !== '—' ? `<span class="hist-topgene">${escapeHtml(s.topGene)}</span>` : '';
  return '<div class="hist-snap">' +
    `<span>👥 ${s.population}</span>` +
    `<span>🔨${s.worker} ⚔${s.soldier} 🪽${s.flyer} ✦${s.specialist}</span>` +
    `<span>🧬 ${s.genes}${asc}</span>` +
    `<span>🗺️ ${s.regionsHeld}</span>` +
    `<span>🏆 #${s.rank}/${s.of}</span>` +
    `<span>⚔️ ${s.military}</span>` +
    top +
    '</div>';
}

// --- Watch mode: notifications + ambient stats overlay ----------------------

/** A sound stinger per major-event type (others stay silent to avoid spam). */
const NOTIF_CUE: Partial<Record<HistoryEvent['type'], CueId>> = {
  apex: 'apexAlert',
  region: 'region',
  discovery: 'discovery',
  milestone: 'objective',
};

/** Pop a major-event banner top-right of the canvas (auto-dismiss; max 4 stacked). */
export function pushNotification(e: HistoryEvent): void {
  const cont = byId('notifications');
  const div = document.createElement('div');
  div.className = `notif type-${e.type}`;
  div.innerHTML =
    `<span class="notif-icon">${EVENT_META[e.type].icon}</span>` +
    `<span class="notif-text"><b>${escapeHtml(e.title)}</b><span class="muted"> · tick ${e.tick}</span></span>`;
  cont.appendChild(div);
  while (cont.childElementCount > 4 && cont.firstChild) cont.removeChild(cont.firstChild);
  const cue = NOTIF_CUE[e.type];
  if (cue !== undefined) audio.play(cue);
  setTimeout(() => { div.classList.add('leaving'); setTimeout(() => div.remove(), 400); }, 5200);
}

/**
 * Pop an Evolution-Box discovery banner (same feed as `pushNotification`, its own
 * styling). Big tiers linger longer so an Apex/Miracle moment stays on screen.
 */
export function pushDiscovery(d: Discovery): void {
  const cont = byId('notifications');
  const div = document.createElement('div');
  div.className = `notif discovery disc-${d.cls}`;
  div.innerHTML =
    `<span class="notif-icon">${d.icon}</span>` +
    `<span class="notif-text"><b>${escapeHtml(d.title)}</b></span>`;
  cont.appendChild(div);
  while (cont.childElementCount > 4 && cont.firstChild) cont.removeChild(cont.firstChild);
  const life = d.cls === 'miracle' ? 7200 : d.cls === 'apex' ? 6000 : 5200;
  setTimeout(() => { div.classList.add('leaving'); setTimeout(() => div.remove(), 400); }, life);
}

/** Ambient "leave it running" stat panel overlaid on the canvas. */
export function updateWatchOverlay(session: GameSession, watch: WatchView | null): void {
  const info = session.playerInfo();
  const { rank, of } = session.playerRank();
  const cam = watch
    ? `<div class="wo-row"><span class="wo-k">Camera</span><span class="wo-v">${escapeHtml(watch.modeLabel())}${watch.observedCaste >= 0 ? ` · 👁 ${escapeHtml(casteName(watch.observedCaste))}` : ''}</span></div>`
    : '';
  byId('watch-overlay-body').innerHTML =
    `<div class="wo-row"><span class="wo-k">Tick</span><span class="wo-v">${session.tick}</span></div>` +
    `<div class="wo-row"><span class="wo-k">Population</span><span class="wo-v">${info.population}</span></div>` +
    `<div class="wo-row"><span class="wo-k">Castes</span><span class="wo-v">🔨${info.castes.worker} ⚔${info.castes.soldier} 🪽${info.castes.flyer} ✦${info.castes.specialist}</span></div>` +
    `<div class="wo-row"><span class="wo-k">Genes</span><span class="wo-v">${info.genes}</span></div>` +
    `<div class="wo-row"><span class="wo-k">Region</span><span class="wo-v">${escapeHtml(session.currentRegionName())}</span></div>` +
    `<div class="wo-row"><span class="wo-k">Rank</span><span class="wo-v">#${rank}/${of}</span></div>` +
    `<div class="wo-row"><span class="wo-k">Military</span><span class="wo-v">${info.military}</span></div>` +
    cam;
}

/** The Evolution Box panel: energy bar, next-box indicator, sources, history. */
export function updateEvolution(session: GameSession): void {
  const st = session.boxState();
  const bd = session.energyBreakdown();

  byId('evo-box-art').className = `box-art tier-${st.tier}`;
  setText('evo-box-tier', st.tierName);

  const btn = byId<HTMLButtonElement>('btn-open-box');
  btn.disabled = !st.ready;
  btn.textContent = st.ready ? 'OPEN EVOLUTION BOX' : 'Charging…';
  btn.classList.toggle('ready', st.ready);
  // Fire the command-center glow/pulse only when a box is charged.
  byId('evo-cmd').classList.toggle('ready', st.ready);

  // Current target line — the caste this box favors, surfaced as the headline goal so the
  // command center reads as "working toward <Caste>", not a faceless meter. Blank otherwise.
  const target = session.nextBoxFavoredCaste();
  const targetEl = byId('evo-target');
  if (target >= 0) {
    const tlay = session.equipmentLayout(target);
    const tglyph = CASTE_GLYPH[target] ?? '◆';
    targetEl.innerHTML = `<span class="evo-target-glyph">${tglyph}</span><b>${escapeHtml(tlay.casteName)} Upgrades Needed</b>`;
  } else {
    targetEl.innerHTML = '';
  }

  // Build focus — the caste this box most advances, so opening reads as progress toward a
  // build rather than a random stat. Mirrors the box's own adaptive bias (favored caste).
  const favored = session.nextBoxFavoredCaste();
  const focusEl = byId('evo-focus');
  if (favored >= 0) {
    const lay = session.equipmentLayout(favored);
    const glyph = CASTE_GLYPH[favored] ?? '•';
    focusEl.innerHTML = lay.occupied >= lay.max
      ? `<span class="muted small">This box favors <b class="evo-focus-name">${glyph} ${escapeHtml(lay.casteName)}</b></span>`
      : `<span class="muted small">Building toward <b class="evo-focus-name">${glyph} ${escapeHtml(lay.casteName)}</b> ` +
        `<span class="evo-focus-prog">${lay.occupied}/${lay.cap} slots</span> — this box favors them</span>`;
  } else {
    focusEl.innerHTML = '';
  }

  setText('evo-energy-num', `${Math.max(0, st.current)} / ${st.cost}`);
  const fill = byId('evo-energy-fill');
  fill.style.width = `${Math.round(st.progress * 100)}%`;
  fill.classList.toggle('full', st.ready);

  byId('evo-energy-srcs').innerHTML =
    energySrc('⚔', bd.kill, 'enemy / wildlife kills') +
    energySrc('🚩', bd.war, 'territory wars won') +
    energySrc('🏠', bd.hive, 'hive nodes founded') +
    energySrc('🗺', bd.region, 'regions unlocked') +
    (bd.project > 0 ? energySrc('🏗', bd.project, 'projects completed') : '') +
    energySrc('★', bd.board, 'leaderboard standing') +
    (bd.bonus > 0 ? energySrc('♻', bd.bonus, 'duplicate conversions') : '');

  setText('evo-stats', `${st.opened} opened · pity ${st.pityAberration}`);

  const hist = session.boxHistory(8);
  byId('evo-history').innerHTML = hist.length
    ? hist.map((h) => `<li><span class="tier-dot tier-${h.tier}"></span>` +
        `<span>${escapeHtml(h.tierName)}</span>` +
        `<span class="muted">${escapeHtml(topReward(h))}</span></li>`).join('')
    : '<li class="muted small">No boxes opened yet.</li>';
}

function energySrc(glyph: string, value: number, title: string): string {
  return `<span title="${title}">${glyph} ${value}</span>`;
}

function topReward(r: OpenResult): string {
  if (r.genes.length === 0) return `+${r.refunded}⚡`;
  let best = r.genes[0] as RewardGene;
  for (const g of r.genes) if (g.rarity > best.rarity) best = g;
  return shortRewardName(best.type, best.rarity);
}

/**
 * One reward card for the opening reveal sequence (used by main.ts) — the SAME
 * collectible look as the inventory card (web/geneCard.ts): rarity band · name · "<Caste>
 * Gene" · Power N (+ the upgrade-vs-held % delta) · the human +N% stat lines · Generation
 * N · the gain forecast. The flourish band (fl-0…3) scales the card's glow/shine with
 * rarity, and a per-rarity reveal class (rv-common…rv-apex) keys the burst FX in the box
 * sequence. `generation` is the colony's current generation (context); omit to hide it.
 */
export function rewardCardHTML(g: RewardGene, isWish = false, generation?: number): string {
  const fl = rarityFlourish(g.rarity);
  // Caste FIT: the full compatibility label (e.g. "Soldier" or "Worker + Flyer (Hybrid)"),
  // so a multi-caste gene reads as the asset it is — not just its primary caste.
  const compat = casteCompatLabel(g.type);
  const casteLine = compat.includes('(Hybrid)') ? compat : `${compat} Gene`;
  const pk = rarityPerk(g.type, g.rarity);
  const perk = pk
    ? `<div class="rperk${pk.special ? ' special' : ''}"><b>${escapeHtml(pk.name)}</b> — ${escapeHtml(pk.effect)}</div>`
    : '';
  return `<div class="reward gc-card rcol-${g.rarity} fl-${fl} ${rarityRevealClass(g.rarity)}${g.duplicate ? ' dup' : ''}${isWish ? ' wish' : ''}">` +
    (isWish ? '<span class="rwish" title="A gene from your Evolution Wishlist">★ WISHLIST</span>' : '') +
    `<div class="inv-row1">${rarityBandHTML(g.rarity)}<span class="inv-row1-right">${powerHTML(g.power)}</span></div>` +
    `<div class="gc-name">${escapeHtml(shortRewardName(g.type, g.rarity))}</div>` +
    `<div class="gc-caste">${escapeHtml(casteLine)}</div>` +
    `<div class="rpower">${powerDeltaHTML(g)}</div>` +
    statLinesHTML(g.type, g.rarity, g.traitTotal) +
    generationHTML(generation) +
    perk +
    gainForecastHTML(g) +
    adviceBadge(g.advice) +
    `<div class="rmeta">${g.duplicate ? '♻ converted to energy' : 'added to inventory'}</div>` +
    (fl >= 2 ? '<span class="rshine"></span>' : '') +
    `</div>`;
}

/** Per-rarity reveal-effect class for a box card (Common glow … Apex/Legendary explosion).
 *  Drives the @keyframes burst keyed off each card's rarity in the opening sequence. */
export function rarityRevealClass(rarity: number): string {
  return rarity >= 4 ? 'rv-apex'
    : rarity >= 3 ? 'rv-epic'
    : rarity >= 2 ? 'rv-rare'
    : rarity >= 1 ? 'rv-uncommon'
    : 'rv-common';
}

/** Steady, band-bounded "% of caste power" this reward adds when applied — the relative
 *  power-progression read that keeps a lower-rarity gene from looking obsolete beside a
 *  higher one (a Common still reads as a real, if small, step forward). */
function gainForecastHTML(g: RewardGene): string {
  if (g.duplicate || g.gainPct === undefined) return '';
  const shown = g.gainPct >= 10 ? Math.round(g.gainPct).toString() : g.gainPct.toFixed(1);
  return `<div class="rgain" title="Projected gain to this gene's caste — relative to current power, bounded by rarity">≈ +${shown}% caste power</div>`;
}

/**
 * Upgrade-comparison chip vs the gene you currently hold in this slot: the difference and
 * the PERCENT improvement, colour-coded by grade (green meaningful / yellow minor / gray
 * sidegrade / red downgrade), with the incumbent shown so the player judges the UPGRADE,
 * not the raw number. The reward card already prints the new "Power N" before this.
 */
function powerDeltaHTML(g: RewardGene): string {
  const c = g.comparison;
  if (!c) return '';
  if (c.isNew) {
    return ` <span class="pdelta new" title="New slot — you hold nothing like it yet, so it is a pure gain">★ NEW</span>`;
  }
  const glyph = c.grade === 'downgrade' ? '▼' : c.grade === 'sidegrade' ? '—' : '▲';
  const sign = c.delta >= 0 ? '+' : '';
  const pctStr = c.incumbentPower > 0 ? ` (${sign}${c.pct}%)` : '';
  const vs = c.incumbentPower > 0 ? ` <span class="pvs">vs ${c.incumbentPower} held</span>` : '';
  const title = `Current ${c.incumbentPower} → New ${g.power} · ${sign}${c.delta}${pctStr}`;
  return ` <span class="pdelta ${c.grade}" title="${title}">${glyph} ${sign}${c.delta}${pctStr}</span>${vs}`;
}

/** Refresh every HUD panel at once. */
export function updateAll(session: GameSession): void {
  updateTopbar(session);
  updateColonyPower(session);
  updateColonyPanel(session);
  updateGenerations(session);
  updateObjectives(session);
  updateLeaderboard(session);
  updateRegions(session);
  updateProjects(session);
  updateWildlife(session);
  updateApex(session);
  updateEvolution(session);
  updateEquipment(session);
  updateGenes(session);
}

export function casteName(caste: number): string {
  return CASTE_NAME[caste] ?? `caste ${caste}`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (ch) =>
    ch === '&' ? '&amp;' : ch === '<' ? '&lt;' : ch === '>' ? '&gt;' : ch === '"' ? '&quot;' : '&#39;');
}
